import { useEffect, useMemo, useState } from "react";
import { Eye, FileText, Save } from "lucide-react";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { Alert, Button, Card, CardContent, EmptyState, Field, PageHeader, Spinner, Textarea, toast } from "@/components/ui";
import { useAuthStore } from "@/stores/auth-store";
import { usePrompts, useUpsertPrompt, usePreviewPrompt } from "./hooks";

export function PromptsPage() {
  const me = useAuthStore((s) => s.user);
  const isSuper = me?.role === "super_admin";
  const [scope, setScope] = useState<string | undefined>(undefined);

  const list = usePrompts(scope);
  const upsert = useUpsertPrompt(scope);
  const preview = usePreviewPrompt(scope);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = useMemo(
    () => list.data?.data.find((p) => p.key === selectedKey) ?? null,
    [list.data, selectedKey],
  );

  const [template, setTemplate] = useState("");
  const [varsJson, setVarsJson] = useState("{}");
  const [runtimeJson, setRuntimeJson] = useState("{}");
  const [rendered, setRendered] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (selected) {
      setTemplate(selected.template);
      setVarsJson(selected.variables_json || "{}");
      setRendered("");
      setErr(null);
    }
  }, [selected]);

  const handleSave = async () => {
    if (!selectedKey) return;
    setErr(null);
    try {
      JSON.parse(varsJson || "{}");
    } catch (e) {
      setErr(`variables_json 不是合法 JSON: ${e instanceof Error ? e.message : ""}`);
      return;
    }
    try {
      await upsert.mutateAsync({ key: selectedKey, payload: { template, variables_json: varsJson } });
      toast.success(`已保存 ${selectedKey}`);
    } catch (e) {
      setErr(e instanceof ApiError ? ((e.body as { detail?: string } | null)?.detail || `保存失败 (${e.status})`) : "保存失败");
    }
  };

  const handlePreview = async () => {
    if (!selectedKey) return;
    setErr(null);
    let runtime: Record<string, unknown> = {};
    try {
      runtime = JSON.parse(runtimeJson || "{}");
    } catch (e) {
      setErr(`runtime_vars 不是合法 JSON: ${e instanceof Error ? e.message : ""}`);
      return;
    }
    try {
      const r = await preview.mutateAsync({
        key: selectedKey,
        payload: { template, variables_json: varsJson, runtime_vars: runtime },
      });
      setRendered(r.rendered);
    } catch (e) {
      setErr(e instanceof ApiError ? ((e.body as { detail?: string } | null)?.detail || `预览失败 (${e.status})`) : "预览失败");
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        title="提示词管理"
        description="编辑各环节使用的提示词模板与默认变量，可先预览渲染结果再保存。"
        actions={
          isSuper && (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={scope === "global"}
                onChange={(e) => setScope(e.target.checked ? "global" : undefined)}
              />
              管理全局默认 (company_id=0)
            </label>
          )
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr] md:items-start">
        <Card className="p-2 md:sticky md:top-0">
          <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
            {list.isLoading ? <><Spinner className="h-3.5 w-3.5" /> 加载中…</> : `${list.data?.data.length ?? 0} 个提示词`}
          </div>
          <ul className="max-h-[40vh] space-y-0.5 overflow-y-auto md:max-h-[70vh]">
            {list.data?.data.map((p) => {
              const active = selectedKey === p.key;
              return (
                <li key={p.key}>
                  <button
                    type="button"
                    onClick={() => setSelectedKey(p.key)}
                    aria-current={active || undefined}
                    className={cn(
                      "w-full truncate rounded-md px-3 py-2 text-left font-mono text-xs transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active ? "bg-primary-soft font-medium text-primary" : "text-foreground hover:bg-muted",
                    )}
                    title={p.key}
                  >
                    {p.key}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card className="min-w-0">
          {!selected ? (
            <EmptyState icon={FileText} title="从左侧选择一个 key 来编辑" />
          ) : (
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="truncate font-mono text-sm font-semibold">{selected.key}</h2>
                <div className="flex gap-2">
                  <Button variant="outline" icon={<Eye />} onClick={handlePreview} loading={preview.isPending}>
                    预览
                  </Button>
                  <Button icon={<Save />} onClick={handleSave} loading={upsert.isPending}>
                    {upsert.isPending ? "保存中…" : "保存"}
                  </Button>
                </div>
              </div>

              <Field label="template">
                <Textarea
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  rows={14}
                  className="font-mono text-xs md:text-xs"
                />
              </Field>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="variables_json" hint="默认变量">
                  <Textarea
                    value={varsJson}
                    onChange={(e) => setVarsJson(e.target.value)}
                    rows={6}
                    className="font-mono text-xs md:text-xs"
                  />
                </Field>
                <Field label="runtime_vars" hint="预览时注入">
                  <Textarea
                    value={runtimeJson}
                    onChange={(e) => setRuntimeJson(e.target.value)}
                    rows={6}
                    className="font-mono text-xs md:text-xs"
                  />
                </Field>
              </div>

              {err && <Alert>{err}</Alert>}

              {rendered && (
                <div className="space-y-1.5">
                  <div className="text-sm font-medium text-foreground">渲染结果</div>
                  <pre className="max-h-64 w-full overflow-y-auto whitespace-pre-wrap rounded-lg border bg-muted/40 px-3 py-2 text-xs text-foreground">
                    {rendered}
                  </pre>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
